import React, { useMemo } from 'react';
import { css } from '@emotion/react';
import { Grid } from '@material-ui/core';
import { genAttributes } from '@onward-delivery/core';
import { DROPOFF_TYPES } from '@/components/ShipmentForm/constants/dropoffOptions';
import { COMPLETION_SOURCES } from '@/constants/completionSources';
import { toNational, toE164 } from '@/utilities/formatPhoneNumber';
import { Header1, Body1, Tag, Step, SubStepDesktop, SubStepMobile, Anchor, Header2 } from './blocks';
import { formatInTimeZone } from 'date-fns-tz';
import { asDateInTZ } from '@/utilities/convertToISO';
import { RETURN_TO_SENDER_DELIVERY } from './itemEvents';

export default function ReturnToSender({
    isDesktop,
    isLoading,
    warehouse,
    before,
    after,
    order,
    callbacks,
    tz,
    readOnly,
}) {
    const { street, unit, city, state, zip, location_type, location } = genAttributes(order, 'ORIGIN');

    const fullAddress = [street, unit, city, state, zip]
        .map((attr) => order[attr])
        .filter((x) => !!x)
        .join(', ');

    const STEPS = useMemo(() => {
        return [
            ...(isDesktop
                ? [
                      {
                          description: `1. Upload middle mile proof of delivery documents`,
                          complete: ({ order, before, after }) => {
                              return !!order?.middle_mile_pod?.length;
                          },
                          ...(!readOnly
                              ? {
                                    cta: 'Upload MM POD',
                                    callback: () => {
                                        callbacks.gotoFileTab();
                                    },
                                }
                              : {}),
                      },
                  ]
                : []),
            {
                description: isDesktop ? (
                    `2. Mark order as 'Returned to Sender'`
                ) : (
                    <>
                        <Grid direction="row">
                            <Body1>{order.middle_mile_origin_name}</Body1>
                        </Grid>
                        <Grid direction="row">
                            {order.middle_mile_origin_phone ? (
                                <Anchor href={`tel:${toE164(order.middle_mile_origin_phone)}`}>
                                    {toNational(order.middle_mile_origin_phone)}
                                </Anchor>
                            ) : (
                                <Body1>--</Body1>
                            )}
                        </Grid>
                        <Grid direction="row">
                            <Body1>{fullAddress}</Body1>
                        </Grid>
                        <Grid direction="row">
                            <Body1>{`${order[location]}, ${
                                DROPOFF_TYPES[order[location]]
                                    ?.find((o) => o.value === order[location_type])
                                    ?.label() || '--'
                            }`}</Body1>
                        </Grid>
                    </>
                ),
                complete: ({ order, before, after }) => {
                    return !!after && ['complete', 'routedComplete'].includes(order.order_status);
                },
                ...(!readOnly
                    ? {
                          cta: 'Mark Complete',
                          callback: ({ order, before, after }) => {
                              const NOW = new Date();
                              const prompt = (
                                  <>
                                      <Grid
                                          direction="row"
                                          container
                                          css={css`
                                              margin-bottom: 12px;
                                          `}
                                      >
                                          <Body1>
                                              Are you sure you want to mark the order as 'Returned to Sender'?
                                          </Body1>
                                      </Grid>
                                      <Grid direction="row" container>
                                          <Body1>
                                              {
                                                  'This step will close out the order and let the customer and shipper know the return has been completed.'
                                              }
                                          </Body1>
                                      </Grid>
                                  </>
                              );
                              const callback = (orderUpdates = {}, itemTrackingEvents) =>
                                  callbacks.upsertEvent({
                                      event: {
                                          ...(after?.event_id
                                              ? { event_id: after.event_id, action: after.action }
                                              : {
                                                    action: 'RETURN_TO_SENDER:SUCCESS',
                                                }),
                                          order_id: order.order_id,
                                          delivery_date: orderUpdates?.completion_time || NOW.toISOString(),
                                      },
                                      orderId: order.order_id,
                                      order: {
                                          order_status: 'complete',
                                          completion_time: NOW.toISOString(),
                                          completion_source: COMPLETION_SOURCES.WEB,
                                          ...orderUpdates,
                                      },
                                      trackingEvents: itemTrackingEvents || [],
                                  });

                              callbacks.openChecklist(
                                  {
                                      content: prompt,
                                      documents: [
                                          { field: 'middle_mile_pod', name: 'Middle Mile POD', required: true },
                                      ],
                                      timestamps: [{ field: 'completion_time', name: 'Completion', required: true }],
                                      title: 'Deliver Order',
                                      // Arrived back at the sender. No return swap — see itemEvents.js.
                                      ...RETURN_TO_SENDER_DELIVERY,
                                      itemsHelperText: 'Checkmark each item after unloading.',
                                  },
                                  callback
                              );
                          },
                      }
                    : {}),
            },
        ];
    }, [isDesktop, location_type, location, order]);

    const isComplete = useMemo(() => {
        return STEPS.map((step) => step.complete({ order, before, after }));
    });

    const overrideComplete = useMemo(() => {
        return !!after && ['complete', 'routedComplete'].includes(order.order_status);
    }, [before, after, order]);

    const inProgressIdx = useMemo(() => {
        return isComplete.findIndex((x) => !x);
    }, [isComplete]);

    const allComplete = isComplete.every((x) => x);
    const inProgress = !!before && !(allComplete || overrideComplete);

    return (
        <Step
            isDesktop={isDesktop}
            isComplete={allComplete || overrideComplete}
            inProgress={inProgress}
            isLast={true}
            label={() => {
                return (
                    <Grid
                        direction="row"
                        container
                        css={css`
                            align-items: center;
                        `}
                    >
                        <Grid
                            direction="column"
                            css={css`
                                margin-right: 8px;
                            `}
                        >
                            <Header1>Returned to Sender</Header1>
                        </Grid>
                        <Grid
                            direction="column"
                            css={css`
                                margin-right: 8px;
                            `}
                        >
                            {!!after ? (
                                <Tag>
                                    <Body1
                                        css={css`
                                            margin-right: 20px;
                                        `}
                                    >
                                        {`Completed: ${
                                            order.completion_time
                                                ? formatInTimeZone(
                                                      order.completion_time,
                                                      tz,
                                                      'EEE, MMM dd, yyyy h:mm a zzz'
                                                  )
                                                : 'Unknown'
                                        }`}
                                    </Body1>
                                    {!readOnly && (
                                        <Anchor
                                            onClick={() => {
                                                const NOW = new Date();
                                                const prompt = (
                                                    <>
                                                        <Grid
                                                            direction="row"
                                                            container
                                                            css={css`
                                                                margin-bottom: 12px;
                                                            `}
                                                        >
                                                            <Body1>
                                                                Are you sure you want to mark the order as 'Returned to
                                                                Sender'?
                                                            </Body1>
                                                        </Grid>
                                                        <Grid direction="row" container>
                                                            <Body1>
                                                                This step will close out the order and let the customer
                                                                and shipper know the return has been completed.
                                                            </Body1>
                                                        </Grid>
                                                    </>
                                                );
                                                const callback = (orderUpdates = {}, itemTrackingEvents) =>
                                                    callbacks.upsertEvent({
                                                        event: {
                                                            ...(after?.event_id
                                                                ? { event_id: after.event_id, action: after.action }
                                                                : {
                                                                      action: 'RETURN_TO_SENDER:SUCCESS',
                                                                  }),
                                                            order_id: order.order_id,
                                                            delivery_date:
                                                                orderUpdates?.completion_time || NOW.toISOString(),
                                                        },
                                                        orderId: order.order_id,
                                                        order: {
                                                            order_status: 'complete',
                                                            completion_time: NOW.toISOString(),
                                                            completion_source: COMPLETION_SOURCES.WEB,
                                                            ...orderUpdates,
                                                        },
                                                        trackingEvents: itemTrackingEvents || [],
                                                    });

                                                callbacks.openChecklist(
                                                    {
                                                        content: prompt,
                                                        event: after,
                                                        documents: [
                                                            {
                                                                field: 'middle_mile_pod',
                                                                name: 'Middle Mile POD',
                                                                required: true,
                                                            },
                                                        ],
                                                        timestamps: [
                                                            {
                                                                field: 'completion_time',
                                                                name: 'Completion',
                                                                required: true,
                                                            },
                                                        ],
                                                        title: 'Deliver Order',
                                                        // Same step as above, reached via the Edit anchor.
                                                        ...RETURN_TO_SENDER_DELIVERY,
                                                        itemsHelperText: 'Checkmark each item after unloading.',
                                                    },
                                                    callback
                                                );
                                            }}
                                        >
                                            <Header1>Edit</Header1>
                                        </Anchor>
                                    )}
                                </Tag>
                            ) : null}
                        </Grid>
                    </Grid>
                );
            }}
        >
            {() => (
                <>
                    {STEPS.map(({ description, cta, secondaryCta, callback, secondaryCallback }, idx) => {
                        const SubStep = isDesktop ? SubStepDesktop : SubStepMobile;

                        return (
                            <SubStep
                                key={idx}
                                isComplete={isComplete[idx]}
                                inProgress={inProgressIdx === idx}
                                isLoading={isLoading}
                                isLast={idx === STEPS.length - 1}
                                description={description}
                                cta={cta}
                                secondaryCta={secondaryCta}
                                secondaryOrange={true}
                                callback={
                                    callback
                                        ? () => {
                                              callback({ before, after, order });
                                          }
                                        : null
                                }
                                secondaryCallback={
                                    secondaryCallback
                                        ? () => {
                                              secondaryCallback({ before, after, order });
                                          }
                                        : null
                                }
                            />
                        );
                    })}
                </>
            )}
        </Step>
    );
}
